import React, { useState, useEffect, useRef } from 'react';
import { Artist, CreateArtistRequest, Country, AssignArtistRequest } from '../../models/artist';
import ArtistPopup from './ArtistPopup';
import ArtistSearch from './ArtistSearch';
import '../../styles/components.css';
import { Button, Typography, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArtistAssignment from './ArtistAssignment';
import { ArtistSource } from '../../models/Artwork';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";
import { createApiClient, useApiClient } from "../../components/useApiClient";

//Standard Api
import axios from "axios";

interface ArtistSelectorProps {
    selectedArtist?: Artist | null;
    onArtistSelect: (artist: Artist | null) => void;
    allowCreate?: boolean;
    allowSearch?: boolean;
    required?: boolean;
    disabled?: boolean;
    className?: string;
}



const ArtistSelector: React.FC<ArtistSelectorProps> = ({
    selectedArtist,
    onArtistSelect,
    allowCreate = true,
    allowSearch = true,
    required = false,
    disabled = false,
    className = ''
}) => {

    type dialogModeType = "view" | "edit" | "assign" | "create";
    const [dialogMode, setDialogMode] = useState<dialogModeType>("view");
    const [viewArtist, setViewArtist] = useState<Artist | null>(null);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { put, post, delete: del } = useApiClient();

    const [artists, setArtists] = useState<Artist[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);
    const isAuthenticated = useIsAuthenticated();
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const handleCreateNewArtist = () => {
        setDialogMode("create");
        setViewArtist(null);
        setIsPopupOpen(true);
    };

    const handleUpdateArtist = async (artistData: Artist) => {

        try {
            //const formData = JSON.stringify(artistData);

            const bio = artistData.source == ArtistSource.User ? artistData.biography : artistData.clientBio;

            const formData = new FormData();
            formData.append("ArtistId", artistData.artistId.toString());
            formData.append("ArtistName", artistData.artistName);
            formData.append("BirthDate", artistData.birthDate ?? '');
            formData.append("DeathDate", artistData.deathDate ?? '');
            formData.append("ArtistPeriod", artistData.artistPeriod ?? '');
            formData.append("CountryCode", artistData.countryCode ?? '');
            formData.append("Source", artistData.source.toString());
            formData.append("Biography", bio ?? '');
            formData.append("ClientBio", bio ?? '');


            const accessToken = await getAccessToken();

            if (accessToken) {

                if (artistData.artistId == 0) {
                    const response = await post<Artist>(`artists/add-to-client/${clientIdRef.current}`, accessToken, formData);
                    const newArtist = response;
                    onArtistSelect(newArtist);
                }
                else {
                    const response = await put(`artists/update-client-link/${artistData.artistId}`, accessToken, formData);
                    onArtistSelect(artistData);
                }



                // Refresh the artist list
                await loadArtists();
            }

        } catch (err) {
            console.error('Failed to add', err);
            throw new Error('Failed to add artist');
        }
    };

    const handleAssignArtist = async (request: AssignArtistRequest) => {

        try {
            const body = JSON.stringify(request);
            const accessToken = await getAccessToken();

            if (accessToken) {
                const response = await axios.post(
                    `${BASE_URL}/artists/assign-to-client/${clientIdRef.current}`,
                    body,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'X-Client-Id': activeClientId?.toString() || '',
                            "Content-Type": "application/json"
                        }
                    });

                await loadArtists();
            }
        } catch (err) {
            console.error('Failed to assign', err);
            throw new Error('Failed to assign artist');
        }
    };

    const artistReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const countriesReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const loadAll = async () => {
        try {
            const tasks = [
                artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
                countriesReq.run({ method: "GET", url: `countries` }),
            ];

            const [ar, co] = await Promise.allSettled(tasks);

            if (ar.status === "fulfilled") setArtists(ar.value);
            if (co.status === "fulfilled") setCountries(co.value);
        }
        catch (error) {
            console.error('Failed to load artrists & country data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadArtists = async () => {
        try {
            const tasks = [
                artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
            ];

            const [ar] = await Promise.allSettled(tasks);

            if (ar.status === "fulfilled") setArtists(ar.value);
        }
        catch (error) {
            console.error('Failed to load artists:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, [clientIdRef]);

    return (
        <div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 gap-2">
                <Button
                    variant="contained" color="success" size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsSearchOpen(true);
                    }}
                    startIcon={<SearchIcon /> }
                >
                Search Artists
                </Button>
                <Button
                    variant="contained" size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCreateNewArtist();
                    }}
                    startIcon={<PersonAddIcon /> }
                >
                Add New Artist
                </Button>
            </div>

            <ArtistAssignment
                open={isPopupOpen}
                mode={dialogMode}
                artist={viewArtist}
                onUpdate={handleUpdateArtist}
                onClose={() => setIsPopupOpen(false)}
                countries={countries}
                existingArtists={artists}
            />

            <ArtistSearch
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                onAssignArtist={handleAssignArtist}
                onArtistSelect={onArtistSelect}
                mode="select"
            />
        </div>
    );
}

export default ArtistSelector;